import {Biobook, BiobookChapters, BiobookCounts, BiobookPage, BiobookPreferences} from '../../features/biobook/types';
import {buildURLSearch} from '../utils/apiUtils';
import {api} from './api';
import {biotwinApi} from './biotwinApi';
import {biotwinSettingsApi} from './biotwinSettingsApi';
import {defaultBiotwinSettings} from '../../features/biotwin-settings/utils';

type PatchCollection = ReturnType<typeof api.util.updateQueryData>;

export const biobookApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getBiobookCounts: builder.query<BiobookCounts, {conditionIds?: number[]; labelId?: number; userId: number}>({
      query: (arg) => ({
        url: `/biobook-counts${buildURLSearch(arg)}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, arg) => [{type: 'BiobookCounts', id: arg.userId}],
    }),

    getBiobookChapters: builder.query<BiobookChapters, {conditionIds?: number[]; labelId?: number; userId: number}>({
      query: (arg) => ({
        url: `/biobook-chapters${buildURLSearch(arg)}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, arg) => [{type: 'BiobookChapters', id: arg.userId}],
    }),

    getBiobookPreferences: builder.query<BiobookPreferences, {userId: number}>({
      query: ({userId}) => ({url: `/biobook-preferences?userId=${userId}`, method: 'GET'}),
      providesTags: (_result, _error, arg) => [{type: 'BiobookPreferences', id: arg.userId}],
    }),

    setBiobookPreferences: builder.mutation<
      BiobookPreferences,
      {biobookPreferences: BiobookPreferences; userId: number}
    >({
      query: ({biobookPreferences, userId}) => ({
        url: `/biobook-preferences?userId=${userId}`,
        method: 'PUT',
        body: biobookPreferences,
      }),
      onQueryStarted: async (arg, {dispatch, queryFulfilled, getState}) => {
        let currentFavoritePhraseIds: number[] = [];
        let currentHiddenPhraseIds: number[] = [];

        const {data: biotwinSettings} = await dispatch(
          biotwinSettingsApi.endpoints.getBiotwinSettings.initiate({userId: arg.userId}),
        );
        const {hiddenPhrasesVisible} = biotwinSettings ?? defaultBiotwinSettings;

        const patchResult = dispatch(
          biobookApi.util.updateQueryData('getBiobookPreferences', {userId: arg.userId}, (biobookPreferences) => {
            currentFavoritePhraseIds = biobookPreferences?.favoritePhraseIds.slice() ?? [];
            currentHiddenPhraseIds = biobookPreferences?.hiddenPhraseIds.slice() ?? [];
            return arg.biobookPreferences;
          }),
        );

        const oldFavoritePhraseIds = currentFavoritePhraseIds.filter(
          (phraseId) => !arg.biobookPreferences.favoritePhraseIds.includes(phraseId),
        );
        const newFavoritePhraseIds = arg.biobookPreferences.favoritePhraseIds.filter(
          (phraseId) => !currentFavoritePhraseIds.includes(phraseId),
        );
        const oldHiddenPhraseIds = currentHiddenPhraseIds.filter(
          (phraseId) => !arg.biobookPreferences.hiddenPhraseIds.includes(phraseId),
        );
        const newHiddenPhraseIds = arg.biobookPreferences.hiddenPhraseIds.filter(
          (phraseId) => !currentHiddenPhraseIds.includes(phraseId),
        );

        let patchResultBiobookFavorites: PatchCollection | undefined;

        if (newFavoritePhraseIds.length > 0) {
          dispatch(api.util.invalidateTags([{type: 'BiobookFavorites', id: arg.userId}]));
        } else if (oldFavoritePhraseIds.length > 0) {
          const removedFavoritePhraseId = currentFavoritePhraseIds.find(
            (phraseId) => !arg.biobookPreferences.favoritePhraseIds.includes(phraseId),
          );
          patchResultBiobookFavorites = dispatch(
            biobookApi.util.updateQueryData('getBiobookFavorites', {userId: arg.userId}, (biobookFavorites) => {
              biobookFavorites.splice(
                biobookFavorites.findIndex((phrase) => phrase.phraseId === removedFavoritePhraseId),
                1,
              );
            }),
          );
        }

        let patchResultsBiobook: PatchCollection[] = [];
        let patchResultsBiobookChapters: PatchCollection[] = [];

        if (!hiddenPhrasesVisible) {
          const args = biobookApi.util.selectCachedArgsForQuery(getState(), 'getBiobookCounts');
          patchResultsBiobookChapters = args.map((arg) =>
            dispatch(
              biobookApi.util.updateQueryData('getBiobookCounts', arg, (biobookCounts) => {
                biobookCounts.phraseCount =
                  biobookCounts.phraseCount + oldHiddenPhraseIds.length - newHiddenPhraseIds.length;
              }),
            ),
          );

          if (oldHiddenPhraseIds.length > 0) {
            dispatch(api.util.invalidateTags([{type: 'Biobook', id: arg.userId}]));
          } else if (newHiddenPhraseIds.length > 0) {
            const args = biobookApi.util.selectCachedArgsForQuery(getState(), 'getBiobook');
            patchResultsBiobook = args.map((arg) =>
              dispatch(
                biobookApi.util.updateQueryData('getBiobook', arg, (biobook) => {
                  return biobook?.filter((biobookPage) => !newHiddenPhraseIds.includes(biobookPage.phraseId)) ?? [];
                }),
              ),
            );
          }
        }

        try {
          await queryFulfilled;
        } catch (e) {
          patchResult.undo();
          patchResultBiobookFavorites?.undo();
          patchResultsBiobook.forEach((patchResultBiobook) => patchResultBiobook.undo());
          patchResultsBiobookChapters.forEach((patchResultBiobookChapters) => patchResultBiobookChapters.undo());
        }
      },
    }),

    getBiobookFavorites: builder.query<BiobookPage[], {userId: number}>({
      query: ({userId}) => `/biobook-favorites?userId=${userId}`,
      providesTags: (_result, _error, arg) => [{type: 'BiobookFavorites', id: arg.userId}],
    }),

    deleteBiobookUpdates: builder.mutation<void, {userId: number}>({
      query: ({userId}) => ({url: `/biobook-updates?userId=${userId}`, method: 'DELETE'}),
      onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
        try {
          await queryFulfilled;
          await dispatch(
            biotwinApi.util.updateQueryData('getBiotwin', {id: arg.userId}, (biotwin) => {
              biotwin.biobookUpdatePhraseIds = [];
            }),
          );
          await dispatch(biotwinApi.util.invalidateTags(['Biotwins']));
        } catch (e) {
          console.error(e);
        }
      },
    }),

    getBiobookUpdates: builder.query<BiobookPage[], {userId: number}>({
      query: ({userId}) => ({url: `/biobook-updates?userId=${userId}&pageNumber=1&pageSize=150`, method: 'GET'}),
      providesTags: (_result, _error, arg) => [{type: 'BiobookUpdates', id: arg.userId}],
    }),

    getBiobookPages: builder.query<BiobookPage[], {labelId: number}>({
      query: ({labelId}) => ({url: `/biobook-pages?labelId=${labelId}`, method: 'GET'}),
    }),

    getBiobook: builder.query<
      Biobook,
      {
        userId: number;
        countryIds?: number[];
        institutionIds?: number[];
        labelId?: number;
        phraseTypeIds?: number[];
        conditionIds?: number[];
        pageNumber?: number;
        pageSize?: number;
      }
    >({
      query: ({conditionIds, countryIds, institutionIds, labelId, pageNumber, pageSize, phraseTypeIds, userId}) => ({
        url: `/biobooks${buildURLSearch({
          conditionIds,
          countryIds,
          institutionIds,
          labelId,
          pageNumber,
          pageSize,
          phraseTypeIds,
          userId,
        })}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, arg) => [{type: 'Biobook', id: arg.userId}],
      transformResponse: (response: Biobook) => {
        return (
          response.map((phrase) => ({
            ...phrase,
            matchedConditionNames: phrase.matchedConditionNames.slice().sort(),
          })) ?? []
        );
      },
    }),
  }),
});

export const {
  useDeleteBiobookUpdatesMutation,
  useGetBiobookChaptersQuery,
  useGetBiobookCountsQuery,
  useGetBiobookFavoritesQuery,
  useGetBiobookPagesQuery,
  useGetBiobookUpdatesQuery,
  useGetBiobookPreferencesQuery,
  useSetBiobookPreferencesMutation,
  useGetBiobookQuery,
} = biobookApi;
